import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { auth, db, storage } from '@/lib/firebase';
import { signOut } from 'firebase/auth';
import { collection, getDocs, addDoc, serverTimestamp, doc, deleteDoc, updateDoc, setDoc, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { sanitizeInput, sanitizeObject } from '@/lib/sanitize';
import { 
  Users, 
  LayoutDashboard, 
  Menu, 
  X, 
  Search, 
  Bell, 
  ChevronDown, 
  ChevronRight,
  Edit, 
  Trash2, 
  MoreVertical,
  LogOut,
  Settings,
  Tags,
  Plus,
  FolderOpen,
  Folder,
  Package,
  Loader2,
  Mail,
  MessageSquare,
  Calendar,
  Image as ImageIcon,
  Video,
  Music,
  FileText,
  Copy,
  UploadCloud,
  Share2,
  Key,
  Clock,
  CalendarRange,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

interface UserData {
  id: string;
  email: string;
  role: string;
  createdAt?: any;
}

interface FileData {
  id?: string;
  name: string;
  url: string;
  type: string;
  size: number;
  ownerId: string;
  createdAt?: any;
}

interface CategoryData {
  id: string;
  name: string;
  parentId: string | null;
  createdAt?: any;
}

interface Product {
  id?: string;
  category: string;
  categoryId: string;
  categoryName: string;
  title: string;
  desc: string;
  price: string;
  image: string;
  cta: string;
  link: string;
  badge?: string;
}

interface SocialCredential {
  id?: string;
  name: string;
  platform: 'facebook' | 'instagram' | 'tiktok' | 'x';
  apiKey: string;
  apiSecret?: string;
  accessToken?: string;
}

interface SocialCampaign {
  id?: string;
  title: string;
  credentialId: string;
  days: number;
  postsPerDay: number;
  startDate: string;
  status: 'active' | 'completed';
}

interface SocialPost {
  id?: string;
  title?: string;
  campaignId?: string;
  mediaUrl: string;
  description: string;
  scheduledAt?: any;
  status: 'draft' | 'pending' | 'posted' | 'failed';
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('usuarios');
  const [usersList, setUsersList] = useState<UserData[]>([]);
  const [categoriesList, setCategoriesList] = useState<CategoryData[]>([]);
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [messagesList, setMessagesList] = useState<any[]>([]);
  const [newsletterList, setNewsletterList] = useState<any[]>([]);
  const [consultationsList, setConsultationsList] = useState<any[]>([]);
  const [academyUrl, setAcademyUrl] = useState('https://smartbusiness.academy');
  const [consultingUrl, setConsultingUrl] = useState('/contact');
  const [facebookUrl, setFacebookUrl] = useState('https://facebook.com');
  const [instagramUrl, setInstagramUrl] = useState('https://instagram.com');
  const [tiktokUrl, setTiktokUrl] = useState('https://tiktok.com');
  const [linkedinUrl, setLinkedinUrl] = useState('https://linkedin.com');
  const [savingSettings, setSavingSettings] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // File Manager States
  const [filesList, setFilesList] = useState<FileData[]>([]);
  const [activeFileFilter, setActiveFileFilter] = useState('Todos');
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Auto-Poster States
  const [socialCredentials, setSocialCredentials] = useState<SocialCredential[]>([]);
  const [socialPosts, setSocialPosts] = useState<SocialPost[]>([]);
  const [autoPosterTab, setAutoPosterTab] = useState<'credentials' | 'inventory' | 'scheduler' | 'calendar'>('inventory');
  
  const [newCredential, setNewCredential] = useState<SocialCredential>({ name: '', platform: 'facebook', apiKey: '', apiSecret: '', accessToken: '' });
  
  // Inventory State
  const [draftConfig, setDraftConfig] = useState({ title: '', description: '' });
  const [draftMedia, setDraftMedia] = useState<File | null>(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState<string>('');
  const [showFileSelectorModal, setShowFileSelectorModal] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Scheduler State
  const [campaignConfig, setCampaignConfig] = useState({
    credentialId: '',
    days: 1,
    postsPerDay: 1,
    startDate: new Date().toISOString().slice(0, 10),
    startTime: '10:00'
  });
  const [selectedDraftIds, setSelectedDraftIds] = useState<string[]>([]);
  const [isGeneratingCampaign, setIsGeneratingCampaign] = useState(false);
  
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingSubTo, setAddingSubTo] = useState<string | null>(null);

  // Estados para creación de productos
  const [showProductForm, setShowProductForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [newProduct, setNewProduct] = useState<Omit<Product, 'id'>>({
    category: 'courses',
    categoryId: 'courses',
    categoryName: 'Cursos',
    title: '',
    desc: '',
    price: '',
    image: 'https://readdy.ai/api/search-image?query=digital%20product%20neon%20glow&width=400&height=280',
    cta: 'Comprar Ahora',
    link: 'https://stan.store',
    badge: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch Users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as UserData[];
        setUsersList(users);

        // Fetch Categories
        const catsSnapshot = await getDocs(collection(db, 'categories'));
        const categories = catsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as CategoryData[];
        setCategoriesList(categories);

        // Fetch Products
        const productsSnapshot = await getDocs(collection(db, 'products'));
        const products = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Product[];
        setProductsList(products);

        // Fetch Messages
        const msgsSnapshot = await getDocs(collection(db, 'contact_messages'));
        const msgs = msgsSnapshot.docs.map(doc => ({ id: doc.id, status: 'pendiente', ...doc.data() }));
        setMessagesList(msgs);

        // Fetch Newsletter
        const newsSnapshot = await getDocs(collection(db, 'newsletter_subscribers'));
        const news = newsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setNewsletterList(news);

        // Fetch Consultations
        const consSnapshot = await getDocs(collection(db, 'consultations'));
        const consultations = consSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setConsultationsList(consultations);

        // Fetch Settings
        const settingsSnap = await getDocs(collection(db, 'settings'));
        const settingsData = settingsSnap.docs.find(d => d.id === 'general')?.data();
        if (settingsData) {
          if (settingsData.academyUrl) setAcademyUrl(settingsData.academyUrl);
          if (settingsData.consultingUrl) setConsultingUrl(settingsData.consultingUrl);
          if (settingsData.facebookUrl) setFacebookUrl(settingsData.facebookUrl);
          if (settingsData.instagramUrl) setInstagramUrl(settingsData.instagramUrl);
          if (settingsData.tiktokUrl) setTiktokUrl(settingsData.tiktokUrl);
          if (settingsData.linkedinUrl) setLinkedinUrl(settingsData.linkedinUrl);
        }

        // Fetch User Files
        if (user) {
          const filesQuery = query(collection(db, 'files'), where('ownerId', '==', user.uid));
          const filesSnapshot = await getDocs(filesQuery);
          const files = filesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as FileData[];
          setFilesList(files.sort((a,b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
        }

        // Fetch Auto-Poster Data
        const credsSnapshot = await getDocs(collection(db, 'social_credentials'));
        const creds = credsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as SocialCredential[];
        setSocialCredentials(creds);

        const postsSnapshot = await getDocs(collection(db, 'social_posts'));
        const autoPosts = postsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as SocialPost[];
        setSocialPosts(autoPosts);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = () => {
    signOut(auth);
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole
      });
      setUsersList(usersList.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Error al actualizar el rol.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) return;
    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsersList(usersList.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error al eliminar usuario.');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) return;
    try {
      await deleteDoc(doc(db, 'categories', id));
      setCategoriesList(categoriesList.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error al eliminar categoría.');
    }
  };

  const handleEditCategory = async (id: string, currentName: string) => {
    const newName = window.prompt('Nuevo nombre para la categoría:', currentName);
    if (!newName || newName === currentName) return;
    try {
      await updateDoc(doc(db, 'categories', id), { name: newName });
      setCategoriesList(categoriesList.map(c => c.id === id ? { ...c, name: newName } : c));
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este producto?')) return;
    try {
      await deleteDoc(doc(db, 'products', id));
      setProductsList(productsList.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error al eliminar producto.');
    }
  };

  const [editingProductId, setEditingProductId] = useState<string | null>(null);

  const handleEditProductClick = (product: Product) => {
    setNewProduct(product);
    setEditingProductId(product.id || null);
    setShowProductForm(true);
  };

  const handleUpdateMessageStatus = async (msgId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'contact_messages', msgId), {
        status: newStatus
      });
      setMessagesList(messagesList.map(m => m.id === msgId ? { ...m, status: newStatus } : m));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error al actualizar el estado.');
    }
  };

  const compressImage = (file: File, maxWidth = 800, maxHeight = 800, quality = 0.7): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas is empty'));
            }
          }, 'image/jpeg', quality);
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAddCategory = async (parentId: string | null = null) => {
    if (!newCategoryName.trim()) return;

    const sanitizedName = sanitizeInput(newCategoryName);

    try {
      const docRef = await addDoc(collection(db, 'categories'), {
        name: sanitizedName,
        parentId: parentId,
        createdAt: serverTimestamp()
      });

      const newCat: CategoryData = {
        id: docRef.id,
        name: sanitizedName,
        parentId: parentId
      };
      
      setCategoriesList([...categoriesList, newCat]);
      setNewCategoryName('');
      setAddingSubTo(null);
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleCreateProduct = async () => {
    setUploading(true);
    try {
      let imageUrl = newProduct.image;
      
      // Si hay un archivo seleccionado, lo comprimimos y subimos
      if (imageFile) {
        if (!imageFile.type.startsWith('image/')) {
          alert('Error: El archivo seleccionado no es una imagen válida.');
          setUploading(false);
          return;
        }

        // Eliminar imagen anterior si estamos editando
        if (editingProductId) {
          const oldProduct = productsList.find(p => p.id === editingProductId);
          if (oldProduct && oldProduct.image && oldProduct.image.includes('firebasestorage')) {
            try {
              const oldImageRef = ref(storage, oldProduct.image);
              await deleteObject(oldImageRef);
            } catch (e) {
              console.error('Error deleting old image:', e);
            }
          }
        }

        // Comprimir imagen
        const compressedBlob = await compressImage(imageFile);

        // Cambiar nombre y subir
        const hashName = crypto.randomUUID();
        const storageRef = ref(storage, `products/${hashName}.jpg`);
        const snapshot = await uploadBytes(storageRef, compressedBlob);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      // Sanitizar datos del producto
      const sanitizedProduct = sanitizeObject(newProduct);

      // Encontrar el nombre de la categoría seleccionada
      const selectedCat = categoriesList.find(c => c.id === newProduct.categoryId);
      
      const productToSave = {
        ...sanitizedProduct,
        image: imageUrl,
        category: newProduct.categoryId, // Usamos el ID como categoría para el filtro
        categoryName: selectedCat ? selectedCat.name : 'Sin Categoría'
      };
      
      if (editingProductId) {
        await updateDoc(doc(db, 'products', editingProductId), productToSave);
        setProductsList(productsList.map(p => p.id === editingProductId ? { id: editingProductId, ...productToSave } : p));
        setEditingProductId(null);
      } else {
        const docRef = await addDoc(collection(db, 'products'), {
          ...productToSave,
          createdAt: serverTimestamp()
        });
        setProductsList([...productsList, { id: docRef.id, ...productToSave }]);
      }

      setShowProductForm(false);
      setImageFile(null);
      setNewProduct({
        category: 'courses',
        categoryId: 'courses',
        categoryName: 'Cursos',
        title: '',
        desc: '',
        price: '',
        image: 'https://readdy.ai/api/search-image?query=digital%20product%20neon%20glow&width=400&height=280',
        cta: 'Comprar Ahora',
        link: 'https://stan.store',
        badge: ''
      });
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error al guardar el producto.');
    } finally {
      setUploading(false);
    }
  };

  const getChildren = (parentId: string | null) => {
    return categoriesList.filter(c => c.parentId === parentId);
  };

  // Componente Recursivo para el Árbol de Categorías
  const CategoryNode = ({ category, level = 0 }: { category: CategoryData, level: number }) => {
    const children = getChildren(category.id);
    const [expanded, setExpanded] = useState(true);

    return (
      <div className={`${level > 0 ? 'ml-6 border-l border-slate-700/50 pl-4' : ''} mt-2`}>
        <div className="flex items-center justify-between gap-2 p-3 bg-slate-800/30 hover:bg-slate-700/30 rounded-lg group transition-colors">
          <div className="flex items-center gap-2">
            {children.length > 0 ? (
              <button onClick={() => setExpanded(!expanded)} className="text-slate-400 hover:text-white">
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            ) : (
              <span className="w-4"></span>
            )}
            {children.length > 0 ? <FolderOpen size={16} className="text-cyan-400" /> : <Folder size={16} className="text-slate-500" />}
            <span className="font-medium text-slate-200">{category.name}</span>
          </div>
          
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              onClick={() => setAddingSubTo(category.id)}
              className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/20 transition-colors flex items-center gap-1"
            >
              <Plus size={12} />
              Sub
            </button>
            <button 
              onClick={() => handleEditCategory(category.id, category.name)}
              className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-cyan-400"
              title="Editar Nombre"
            >
              <Edit size={14} />
            </button>
            <button 
              onClick={() => handleDeleteCategory(category.id)}
              className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
              title="Eliminar Categoría"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {addingSubTo === category.id && (
          <div className="ml-6 mt-2 flex gap-2">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder={`Nueva subcategoría en ${category.name}...`}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-emerald-500 w-full"
            />
            <button 
              onClick={() => handleAddCategory(category.id)}
              className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium text-sm transition-colors"
            >
              Añadir
            </button>
            <button 
              onClick={() => {setAddingSubTo(null); setNewCategoryName('');}}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium text-sm transition-colors"
            >
              Cancelar
            </button>
          </div>
        )}

        {expanded && children.length > 0 && (
          <div className="mt-1">
            {children.map(child => (
              <CategoryNode key={child.id} category={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleUpdateConsultationStatus = async (id: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'consultations', id), { status: newStatus });
      setConsultationsList(consultationsList.map(c => c.id === id ? { ...c, status: newStatus } : c));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error al actualizar estado.');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await setDoc(doc(db, 'settings', 'general'), { 
        academyUrl,
        consultingUrl,
        facebookUrl,
        instagramUrl,
        tiktokUrl,
        linkedinUrl
      }, { merge: true });
      alert('Configuración guardada exitosamente.');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error al guardar configuración.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length || !user) return;
    setUploadingFile(true);
    try {
      const file = e.target.files[0];
      const fileType = file.type.startsWith('image/') ? 'image' : 
                       file.type.startsWith('video/') ? 'video' : 
                       file.type.startsWith('audio/') ? 'audio' : 'document';
      
      const ext = file.name.split('.').pop() || 'bin';
      const hashName = crypto.randomUUID();
      const storageRef = ref(storage, `user_files/${user.uid}/${hashName}.${ext}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      
      const newFileData = {
        name: file.name,
        url,
        type: fileType,
        size: file.size,
        ownerId: user.uid,
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'files'), newFileData);
      setFilesList([{ id: docRef.id, ...newFileData }, ...filesList]);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error subiendo archivo');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId: string, fileUrl: string) => {
    if (!confirm('¿Estás seguro de eliminar este archivo?')) return;
    try {
      const fileRef = ref(storage, fileUrl);
      await deleteObject(fileRef);
      await deleteDoc(doc(db, 'files', fileId));
      setFilesList(filesList.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error eliminando archivo');
    }
  };
  
  const handleCopyFileLink = (url: string) => {
    navigator.clipboard.writeText(url);
    alert('Enlace copiado al portapapeles');
  };

  const handleSaveCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const docRef = await addDoc(collection(db, 'social_credentials'), newCredential);
      setSocialCredentials([...socialCredentials, { id: docRef.id, ...newCredential }]);
      setNewCredential({ name: '', platform: 'facebook', apiKey: '', apiSecret: '', accessToken: '' });
      alert('Credencial guardada');
    } catch (error) {
      console.error(error);
      alert('Error al guardar credencial');
    }
  };

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draftMedia && !selectedFileUrl) {
      alert('Selecciona una imagen/video.');
      return;
    }
    
    setIsSavingDraft(true);
    try {
      let mediaUrl = selectedFileUrl;

      if (draftMedia) {
        const ext = draftMedia.name.split('.').pop() || 'bin';
        const storageRef = ref(storage, `campaigns/${crypto.randomUUID()}.${ext}`);
        const snapshot = await uploadBytes(storageRef, draftMedia);
        mediaUrl = await getDownloadURL(snapshot.ref);
      }

      const postData = {
        title: draftConfig.title,
        mediaUrl,
        description: draftConfig.description,
        status: 'draft' as const
      };

      const docRef = await addDoc(collection(db, 'social_posts'), postData);
      setSocialPosts([{ id: docRef.id, ...postData }, ...socialPosts]);
      
      setDraftConfig({ title: '', description: '' });
      setDraftMedia(null);
      setSelectedFileUrl('');
      alert('Borrador guardado exitosamente.');
    } catch (error) {
      console.error(error);
      alert('Error guardando borrador');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleScheduleCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    const requiredPosts = campaignConfig.days * campaignConfig.postsPerDay;
    
    if (selectedDraftIds.length !== requiredPosts) {
      alert(`Debes seleccionar exactamente ${requiredPosts} borradores para esta campaña.`);
      return;
    }
    if (!campaignConfig.credentialId) {
      alert('Selecciona una credencial.');
      return;
    }
    
    setIsGeneratingCampaign(true);
    try {
      const campaignData = {
        title: `Campaña ${new Date().toLocaleDateString()}`,
        credentialId: campaignConfig.credentialId,
        days: Number(campaignConfig.days),
        postsPerDay: Number(campaignConfig.postsPerDay),
        startDate: campaignConfig.startDate,
        status: 'active'
      };
      const campaignRef = await addDoc(collection(db, 'social_campaigns'), campaignData);

      const [year, month, day] = campaignConfig.startDate.split('-').map(Number);
      const [hour, minute] = campaignConfig.startTime.split(':').map(Number);
      let baseDate = new Date(year, month - 1, day, hour, minute, 0);

      let draftIndex = 0;
      const updatedPosts = [...socialPosts];

      for (let i = 0; i < campaignData.days; i++) {
        for (let j = 0; j < campaignData.postsPerDay; j++) {
          const offsetHours = j * (12 / campaignData.postsPerDay);
          const postDate = new Date(baseDate.getTime());
          postDate.setDate(postDate.getDate() + i);
          postDate.setHours(postDate.getHours() + offsetHours);

          const draftId = selectedDraftIds[draftIndex];
          const postRef = doc(db, 'social_posts', draftId);
          
          await updateDoc(postRef, {
            campaignId: campaignRef.id,
            scheduledAt: postDate.toISOString(),
            status: 'pending'
          });

          // Update local state
          const postIndex = updatedPosts.findIndex(p => p.id === draftId);
          if (postIndex !== -1) {
            updatedPosts[postIndex] = {
              ...updatedPosts[postIndex],
              campaignId: campaignRef.id,
              scheduledAt: postDate.toISOString(),
              status: 'pending'
            };
          }
          
          draftIndex++;
        }
      }

      setSocialPosts(updatedPosts);
      setSelectedDraftIds([]);
      alert('Campaña programada exitosamente.');
      setAutoPosterTab('calendar');
    } catch (error) {
      console.error(error);
      alert('Error programando campaña');
    } finally {
      setIsGeneratingCampaign(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050008] text-white flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-800/90 backdrop-blur-xl border-r border-slate-700/50 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } flex flex-col`}
      >
        <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Smart CRM
            </span>
          </div>
          <button className="text-slate-400 hover:text-white" onClick={() => setSidebarOpen(false)}>
            <X size={24} />
          </button>
        </div>
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto custom-scrollbar pb-24">
          <a
            href="/"
            className="w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors bg-purple-600/20 text-purple-400 border border-purple-500/20 hover:bg-purple-600/30 hover:text-white"
          >
            <Edit size={20} />
            Editar Front Site
          </a>
          <button
            onClick={() => setActiveMenu('dashboard')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'dashboard' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <LayoutDashboard size={20} />
            Dashboard
          </button>
          <button
            onClick={() => setActiveMenu('usuarios')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'usuarios' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Users size={20} />
            Usuarios
          </button>
          <button
            onClick={() => setActiveMenu('categorias')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'categorias' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Tags size={20} />
            Categorías
          </button>
          <button
            onClick={() => setActiveMenu('productos')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'productos' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Package size={20} />
            Productos
          </button>
           <button
            onClick={() => setActiveMenu('mensajes')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'mensajes' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <MessageSquare size={20} />
            Mensajes
          </button>
          <button
            onClick={() => setActiveMenu('newsletter')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'newsletter' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Mail size={20} />
            Newsletter
          </button>
          <button
            onClick={() => setActiveMenu('consultas')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'consultas' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Calendar size={20} />
            Consultas
          </button>
          <button
            onClick={() => setActiveMenu('archivos')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'archivos' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <FolderOpen size={20} />
            Archivos
          </button>
          <button
            onClick={() => setActiveMenu('autoposter')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'autoposter' ? 'bg-[#8c52ff]/20 text-[#00c2ff] border border-[#8c52ff]/30' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Share2 size={20} />
            Auto-Poster
          </button>
          <button 
            onClick={() => setActiveMenu('configuracion')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'configuracion' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Settings size={20} />
            Configuración
          </button>
        </nav>
        

      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-h-screen overflow-hidden transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        {/* Header */}
        <header className="bg-slate-800/50 backdrop-blur-md p-4 border-b border-slate-700/50 flex justify-between items-center">
          <button className="text-slate-400 hover:text-white" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu size={24} />
          </button>
          
          <div className="hidden md:flex items-center gap-2 bg-slate-700/50 px-3 py-2 rounded-lg border border-slate-600/50 w-64">
            <Search size={18} className="text-slate-400" />
            <input type="text" placeholder="Buscar..." className="bg-transparent border-none focus:outline-none text-sm text-white placeholder-slate-400 w-full" />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative text-slate-400 hover:text-white group"
              >
                <Bell size={20} />
                {consultationsList.filter(c => c.status === 'Pendiente').length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {consultationsList.filter(c => c.status === 'Pendiente').length}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-3 w-80 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
                  <div className="p-4 border-b border-slate-700 bg-slate-800/80">
                    <h3 className="text-white font-bold text-sm">Notificaciones</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {consultationsList.filter(c => c.status === 'Pendiente').length === 0 ? (
                      <div className="p-6 text-center text-slate-400 text-sm">
                        No tienes nuevas notificaciones
                      </div>
                    ) : (
                      consultationsList
                        .filter(c => c.status === 'Pendiente')
                        .sort((a,b) => b.createdAt?.seconds - a.createdAt?.seconds)
                        .map(c => (
                          <div 
                            key={c.id}
                            onClick={() => {
                              setActiveMenu('consultas');
                              setShowNotifications(false);
                            }}
                            className="p-4 border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                                <Calendar size={14} />
                              </div>
                              <div>
                                <p className="text-sm text-white font-medium">Nueva consulta de {c.firstName}</p>
                                <p className="text-xs text-slate-400 mt-1 line-clamp-1">{c.message}</p>
                                <p className="text-[10px] text-slate-500 mt-2">
                                  {c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000).toLocaleString() : 'Reciente'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                  <div className="p-3 border-t border-slate-700 bg-slate-800/80 text-center">
                    <button 
                      onClick={() => {
                        setActiveMenu('consultas');
                        setShowNotifications(false);
                      }}
                      className="text-emerald-400 text-xs font-medium hover:text-emerald-300"
                    >
                      Ver todas las consultas
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div 
              className="flex items-center gap-3 border-l border-slate-700/50 pl-4 cursor-pointer group"
              onClick={handleLogout}
              title="Cerrar Sesión"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center font-bold text-slate-900 text-sm">
                U
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-white">Admin</p>
                <p className="text-xs text-slate-400 truncate max-w-[150px]">{user?.email}</p>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-400 group-hover:text-red-400 transition-colors">
                <LogOut size={14} />
                <span>Logout</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-6 overflow-y-auto">
          {activeMenu === 'dashboard' && (
            <div>
              <h2 className="text-3xl font-bold mb-6">Overview</h2>
              <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                <p className="text-slate-400">Aquí irían las métricas generales.</p>
              </div>
            </div>
          )}

          {activeMenu === 'usuarios' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Gestión de Usuarios</h2>
                  <p className="text-slate-400 mt-1">Lista de todos los usuarios registrados en el sistema.</p>
                </div>
              </div>

              {/* Table */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Usuario</th>
                        <th className="p-4 font-semibold">Rol</th>
                        <th className="p-4 font-semibold">Estado</th>
                        <th className="p-4 font-semibold">ID</th>
                        <th className="p-4 font-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : (
                        usersList.map((u) => (
                          <tr key={u.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4 flex items-center gap-3">
                              <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center text-sm font-bold text-slate-900">
                                {u.email.substring(0, 1).toUpperCase()}
                              </div>
                              <p className="font-medium text-white">{u.email}</p>
                            </td>
                            <td className="p-4">
                              <select
                                value={u.role}
                                onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                                className="bg-slate-700/50 border border-slate-600 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
                              >
                                <option value="client">client</option>
                                <option value="employee">employee</option>
                                <option value="admin">admin</option>
                              </select>
                            </td>
                            <td className="p-4">
                              <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                Activo
                              </span>
                            </td>
                            <td className="p-4 font-mono text-xs text-slate-500">{u.id.substring(0, 8)}...</td>
                            <td className="p-4 flex justify-center gap-2">
                              <button 
                                onClick={() => handleDeleteUser(u.id)}
                                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
                                title="Eliminar Usuario"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'categorias' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Gestión de Categorías</h2>
                  <p className="text-slate-400 mt-1">Crea categorías y subcategorías ilimitadas hacia abajo.</p>
                </div>
              </div>

              {/* Formulario Raíz */}
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 mb-6 flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Nueva categoría principal..."
                  className="bg-slate-700/50 border border-slate-600 focus:border-emerald-500 rounded-lg px-4 py-2 text-white focus:outline-none w-full"
                />
                <button 
                  onClick={() => handleAddCategory(null)}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  <Plus size={18} />
                  Añadir Raíz
                </button>
              </div>

              {/* Árbol de Categorías */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 p-6 shadow-2xl">
                {categoriesList.filter(c => c.parentId === null).length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No hay categorías. Crea la primera arriba.</p>
                ) : (
                  categoriesList.filter(c => c.parentId === null).map(rootCat => (
                    <CategoryNode key={rootCat.id} category={rootCat} level={0} />
                  ))
                )}
              </div>
            </div>
          )}

          {activeMenu === 'productos' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Gestión de Productos</h2>
                  <p className="text-slate-400 mt-1">Agrega y administra los productos de tu tienda.</p>
                </div>
                <button 
                  onClick={() => setShowProductForm(!showProductForm)}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {showProductForm ? <X size={18} /> : <Plus size={18} />}
                  {showProductForm ? "Cancelar" : "Nuevo Producto"}
                </button>
              </div>

              {/* Formulario de Creación */}
              {showProductForm && (
                <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700/50 mb-6 shadow-2xl">
                  <h3 className="text-xl font-bold mb-4">Crear Nuevo Producto</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Categoría (Desde Firestore)</label>
                      <select 
                        value={newProduct.categoryId}
                        onChange={(e) => setNewProduct({...newProduct, categoryId: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                      >
                        <option value="">Selecciona una categoría</option>
                        {categoriesList.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Título</label>
                      <input 
                        type="text" 
                        value={newProduct.title}
                        onChange={(e) => setNewProduct({...newProduct, title: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        placeholder="Ej: Curso de Instagram"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-slate-400 text-xs block mb-1">Descripción</label>
                      <textarea 
                        value={newProduct.desc}
                        onChange={(e) => setNewProduct({...newProduct, desc: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 h-20"
                        placeholder="Describe el producto..."
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Precio</label>
                      <input 
                        type="text" 
                        value={newProduct.price}
                        onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        placeholder="Ej: $97"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Badge (Opcional)</label>
                      <input 
                        type="text" 
                        value={newProduct.badge}
                        onChange={(e) => setNewProduct({...newProduct, badge: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        placeholder="Ej: Más Vendido"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Subir Imagen</label>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => setImageFile(e.target.files ? e.target.files[0] : null)}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Link de Compra</label>
                      <input 
                        type="text" 
                        value={newProduct.link}
                        onChange={(e) => setNewProduct({...newProduct, link: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        placeholder="Ej: https://stan.store/tuusuario"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <button 
                      onClick={() => setShowProductForm(false)}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition-colors"
                      disabled={uploading}
                    >
                      Cancelar
                    </button>
                    <button 
                      onClick={handleCreateProduct}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors flex items-center gap-2"
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        "Guardar Producto"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Table of Products */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Producto</th>
                        <th className="p-4 font-semibold">Categoría</th>
                        <th className="p-4 font-semibold">Precio</th>
                        <th className="p-4 font-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : productsList.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-400">No hay productos. Crea el primero arriba.</td>
                        </tr>
                      ) : (
                        productsList.map((p) => (
                          <tr key={p.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4 flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                              </div>
                              <div>
                                <p className="font-medium text-white">{p.title}</p>
                                <p className="text-xs text-slate-500 truncate max-w-[200px]">{p.desc}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-xs px-2.5 py-1 rounded-full font-medium border bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                                {p.categoryName}
                              </span>
                            </td>
                            <td className="p-4 font-bold text-emerald-400">{p.price}</td>
                            <td className="p-4 flex justify-center gap-2">
                              <button 
                                onClick={() => handleEditProductClick(p)}
                                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-cyan-400"
                                title="Editar Producto"
                              >
                                <Edit size={16} />
                              </button>
                              <button 
                                onClick={() => handleDeleteProduct(p.id!)}
                                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
                                title="Eliminar Producto"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'mensajes' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Mensajes de Contacto</h2>
                  <p className="text-slate-400 mt-1">Gestiona los mensajes recibidos desde el sitio web.</p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Nombre</th>
                        <th className="p-4 font-semibold">Email</th>
                        <th className="p-4 font-semibold">Mensaje</th>
                        <th className="p-4 font-semibold">Estado</th>
                        <th className="p-4 font-semibold">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : messagesList.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-400">No hay mensajes.</td>
                        </tr>
                      ) : (
                        messagesList.map((m) => (
                          <tr key={m.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4 font-medium text-white">{m.name}</td>
                            <td className="p-4">{m.email}</td>
                            <td className="p-4 text-xs max-w-[300px] truncate" title={m.message}>{m.message}</td>
                            <td className="p-4">
                              <select
                                value={m.status || 'pendiente'}
                                onChange={(e) => handleUpdateMessageStatus(m.id, e.target.value)}
                                className="bg-slate-700/50 border border-slate-600 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
                              >
                                <option value="pendiente">Pendiente</option>
                                <option value="en curso">En Curso</option>
                                <option value="cancelado">Cancelado</option>
                                <option value="finalizado">Finalizado</option>
                              </select>
                            </td>
                            <td className="p-4 font-mono text-xs text-slate-500">{m.ip || 'N/A'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'newsletter' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Suscriptores de Newsletter</h2>
                  <p className="text-slate-400 mt-1">Lista de correos registrados para novedades.</p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Email</th>
                        <th className="p-4 font-semibold">IP</th>
                        <th className="p-4 font-semibold">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={3} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : newsletterList.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="p-8 text-center text-slate-400">No hay suscriptores.</td>
                        </tr>
                      ) : (
                        newsletterList.map((n) => (
                          <tr key={n.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4 font-medium text-white">{n.email}</td>
                            <td className="p-4 font-mono text-xs text-slate-500">{n.ip || 'N/A'}</td>
                            <td className="p-4 text-xs text-slate-400">
                              {n.createdAt?.seconds ? new Date(n.createdAt.seconds * 1000).toLocaleString() : 'N/A'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'consultas' && (
            <div className="animate-fade-in space-y-6 max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-3xl font-bold text-white">Consultas Agendadas</h2>
                  <p className="text-slate-400 text-sm mt-1">Gestión de consultas de clientes.</p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Cliente</th>
                        <th className="p-4 font-semibold">Tipo</th>
                        <th className="p-4 font-semibold">Contacto</th>
                        <th className="p-4 font-semibold">Mensaje</th>
                        <th className="p-4 font-semibold">Fecha</th>
                        <th className="p-4 font-semibold text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : consultationsList.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-slate-400">No hay consultas.</td>
                        </tr>
                      ) : (
                        consultationsList.sort((a,b) => b.createdAt?.seconds - a.createdAt?.seconds).map((c) => (
                          <tr key={c.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4">
                              <div className="font-bold text-white">{c.firstName} {c.lastName}</div>
                              {c.type === 'Empresarial' && (
                                <div className="text-xs text-[#00c2ff]">{c.companyName}</div>
                              )}
                            </td>
                            <td className="p-4 text-sm">
                              <span className={`px-2 py-1 rounded text-xs ${c.type === 'Empresarial' ? 'bg-[#00c2ff]/10 text-[#00c2ff]' : 'bg-[#8c52ff]/10 text-[#8c52ff]'}`}>
                                {c.type}
                              </span>
                            </td>
                            <td className="p-4 text-xs">
                              <div>{c.email}</div>
                              <div className="text-slate-400">{c.phone}</div>
                            </td>
                            <td className="p-4 text-xs max-w-[200px] truncate" title={c.message}>
                              {c.message}
                            </td>
                            <td className="p-4 text-xs text-slate-400">
                              {c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="p-4">
                              <select 
                                value={c.status}
                                onChange={(e) => handleUpdateConsultationStatus(c.id, e.target.value)}
                                className={`w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs focus:outline-none focus:border-emerald-500 ${
                                  c.status === 'Pendiente' ? 'text-red-400' :
                                  c.status === 'Contactado' ? 'text-yellow-400' :
                                  'text-emerald-400'
                                }`}
                              >
                                <option value="Pendiente">Pendiente</option>
                                <option value="Contactado">Contactado</option>
                                <option value="Resuelto">Resuelto</option>
                              </select>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'configuracion' && (
            <div className="animate-fade-in space-y-6 max-w-4xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-3xl font-bold text-white">Configuración del Sistema</h2>
                  <p className="text-slate-400 text-sm mt-1">Ajustes generales del sitio web.</p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 p-6 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-4">Enlaces y Redirecciones</h3>
                
                <div className="space-y-4 max-w-lg">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">URL de la Academia (Smart Business Academy)</label>
                    <input 
                      type="url" 
                      value={academyUrl}
                      onChange={(e) => setAcademyUrl(e.target.value)}
                      placeholder="https://smartbusiness.academy"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                    />
                    <p className="text-xs text-slate-500 mt-1">Este enlace se usará en el botón flotante "Join academy".</p>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">URL de Agendar Consultoría</label>
                    <input 
                      type="text" 
                      value={consultingUrl}
                      onChange={(e) => setConsultingUrl(e.target.value)}
                      placeholder="https://calendly.com/... o /contact"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                    />
                    <p className="text-xs text-slate-500 mt-1">Este enlace se usará en la página de Consulting en los botones de "Book a Consultation".</p>
                  </div>

                  <div className="pt-4 border-t border-slate-700/50">
                    <h4 className="text-sm font-bold text-white mb-4">Redes Sociales</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-slate-300 text-xs mb-1">Facebook URL</label>
                        <input 
                          type="url" 
                          value={facebookUrl}
                          onChange={(e) => setFacebookUrl(e.target.value)}
                          placeholder="https://facebook.com/tu-pagina"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 text-xs mb-1">Instagram URL</label>
                        <input 
                          type="url" 
                          value={instagramUrl}
                          onChange={(e) => setInstagramUrl(e.target.value)}
                          placeholder="https://instagram.com/tu-perfil"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 text-xs mb-1">TikTok URL</label>
                        <input 
                          type="url" 
                          value={tiktokUrl}
                          onChange={(e) => setTiktokUrl(e.target.value)}
                          placeholder="https://tiktok.com/@tu-perfil"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                        />
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white font-bold py-2 px-6 rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    {savingSettings ? <Loader2 size={18} className="animate-spin" /> : <Settings size={18} />}
                    {savingSettings ? 'Guardando...' : 'Guardar Configuración'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'archivos' && (
            <div className="animate-fade-in space-y-6 max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-3xl font-bold text-white">Gestor de Archivos Privado</h2>
                  <p className="text-slate-400 text-sm mt-1">Sube y gestiona tus archivos. Solo tú tienes acceso a ellos.</p>
                </div>
                <div>
                  <input
                    type="file"
                    id="fileUpload"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                  />
                  <label 
                    htmlFor="fileUpload"
                    className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] hover:opacity-90 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg shadow-purple-500/20 cursor-pointer disabled:opacity-50"
                  >
                    {uploadingFile ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                    {uploadingFile ? 'Subiendo...' : 'Subir Archivo'}
                  </label>
                </div>
              </div>

              {/* Filtros */}
              <div className="flex overflow-x-auto gap-2 pb-2">
                {['Todos', 'Imágenes', 'Videos', 'Audios', 'Documentos'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setActiveFileFilter(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      activeFileFilter === filter 
                        ? 'bg-[#8c52ff] text-white' 
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>

              {/* Grid de Archivos */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filesList
                  .filter(f => {
                    if (activeFileFilter === 'Todos') return true;
                    if (activeFileFilter === 'Imágenes') return f.type === 'image';
                    if (activeFileFilter === 'Videos') return f.type === 'video';
                    if (activeFileFilter === 'Audios') return f.type === 'audio';
                    if (activeFileFilter === 'Documentos') return f.type === 'document';
                    return true;
                  })
                  .map(file => (
                  <div key={file.id} className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl overflow-hidden group relative flex flex-col">
                    <div className="aspect-square bg-slate-900/50 flex items-center justify-center relative overflow-hidden">
                      {file.type === 'image' ? (
                        <img src={file.url} alt={file.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : file.type === 'video' ? (
                        <Video size={48} className="text-[#00c2ff]/50" />
                      ) : file.type === 'audio' ? (
                        <Music size={48} className="text-[#8c52ff]/50" />
                      ) : (
                        <FileText size={48} className="text-emerald-500/50" />
                      )}
                      
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button 
                          onClick={() => handleCopyFileLink(file.url)}
                          className="w-10 h-10 bg-white/10 hover:bg-[#8c52ff] rounded-full flex items-center justify-center text-white transition-colors"
                          title="Copiar link para compartir"
                        >
                          <Copy size={18} />
                        </button>
                        <button 
                          onClick={() => handleDeleteFile(file.id!, file.url)}
                          className="w-10 h-10 bg-white/10 hover:bg-red-500 rounded-full flex items-center justify-center text-white transition-colors"
                          title="Eliminar archivo"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-medium text-white truncate" title={file.name}>{file.name}</p>
                      <p className="text-[10px] text-slate-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                ))}
                
                {filesList.length === 0 && (
                  <div className="col-span-full py-12 text-center border-2 border-dashed border-slate-700 rounded-xl">
                    <FolderOpen size={48} className="mx-auto text-slate-600 mb-3" />
                    <p className="text-slate-400 font-medium">No tienes archivos subidos</p>
                    <p className="text-slate-500 text-sm mt-1">Sube tu primer archivo usando el botón superior.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeMenu === 'autoposter' && (
            <div className="animate-fade-in space-y-6 max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-3xl font-bold text-white">Auto-Poster Inteligente</h2>
                  <p className="text-slate-400 text-sm mt-1">Genera y programa publicaciones de forma autónoma.</p>
                </div>
                
                <div className="flex bg-slate-800/50 p-1 rounded-xl border border-slate-700">
                  <button
                    onClick={() => setAutoPosterTab('credentials')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      autoPosterTab === 'credentials' ? 'bg-[#8c52ff] text-white shadow-lg' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Key size={16} /> Credenciales
                  </button>
                  <button
                    onClick={() => setAutoPosterTab('inventory')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      autoPosterTab === 'inventory' ? 'bg-[#8c52ff] text-white shadow-lg' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <ImageIcon size={16} /> Inventario
                  </button>
                  <button
                    onClick={() => setAutoPosterTab('scheduler')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      autoPosterTab === 'scheduler' ? 'bg-[#8c52ff] text-white shadow-lg' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Share2 size={16} /> Programador
                  </button>
                  <button
                    onClick={() => setAutoPosterTab('calendar')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      autoPosterTab === 'calendar' ? 'bg-[#8c52ff] text-white shadow-lg' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <CalendarRange size={16} /> Calendario
                  </button>
                </div>
              </div>

              {autoPosterTab === 'credentials' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Formulario de Nueva Credencial */}
                  <div className="lg:col-span-1 bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 h-fit">
                    <h3 className="text-lg font-bold text-white mb-4">Añadir Conexión</h3>
                    <form onSubmit={handleSaveCredential} className="space-y-4">
                      <div>
                        <label className="block text-slate-300 text-xs mb-1">Nombre de Referencia</label>
                        <input
                          type="text"
                          required
                          value={newCredential.name}
                          onChange={e => setNewCredential({...newCredential, name: e.target.value})}
                          placeholder="Ej. Mi Instagram Principal"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 text-xs mb-1">Plataforma</label>
                        <select
                          value={newCredential.platform}
                          onChange={e => setNewCredential({...newCredential, platform: e.target.value as any})}
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                        >
                          <option value="facebook">Facebook</option>
                          <option value="instagram">Instagram</option>
                          <option value="tiktok">TikTok</option>
                          <option value="x">X (Twitter)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-slate-300 text-xs mb-1">API Key / App ID</label>
                        <input
                          type="text"
                          required
                          value={newCredential.apiKey}
                          onChange={e => setNewCredential({...newCredential, apiKey: e.target.value})}
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 text-xs mb-1">Access Token</label>
                        <input
                          type="password"
                          required
                          value={newCredential.accessToken}
                          onChange={e => setNewCredential({...newCredential, accessToken: e.target.value})}
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00c2ff]"
                        />
                      </div>
                      <button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        Guardar Credencial
                      </button>
                    </form>
                  </div>

                  {/* Lista de Credenciales */}
                  <div className="lg:col-span-2 space-y-4">
                    {socialCredentials.map(cred => (
                      <div key={cred.id} className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                            cred.platform === 'facebook' ? 'bg-blue-600' :
                            cred.platform === 'instagram' ? 'bg-pink-600' :
                            cred.platform === 'tiktok' ? 'bg-black border border-slate-600' : 'bg-slate-900'
                          }`}>
                            <i className={`ri-${cred.platform}-fill text-white`} />
                          </div>
                          <div>
                            <h4 className="font-bold text-white text-lg">{cred.name}</h4>
                            <p className="text-slate-400 text-xs uppercase tracking-wider">{cred.platform}</p>
                          </div>
                        </div>
                        <div className="text-emerald-400 flex items-center gap-2 text-sm bg-emerald-400/10 px-3 py-1 rounded-full">
                          <CheckCircle2 size={16} /> Conectado
                        </div>
                      </div>
                    ))}
                    {socialCredentials.length === 0 && (
                      <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-xl">
                        <Key size={48} className="mx-auto text-slate-600 mb-3" />
                        <p className="text-slate-400 font-medium">No hay credenciales configuradas.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {autoPosterTab === 'inventory' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Creador de Borradores */}
                  <div className="lg:col-span-1 bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 h-fit">
                    <h3 className="text-xl font-bold text-white mb-4">Crear Borrador</h3>
                    <form onSubmit={handleSaveDraft} className="space-y-4">
                      <div>
                        <label className="block text-slate-300 text-sm font-bold mb-2">Nombre Interno (Opcional)</label>
                        <input
                          type="text"
                          value={draftConfig.title}
                          onChange={e => setDraftConfig({...draftConfig, title: e.target.value})}
                          placeholder="Ej. Promo Verano 1"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c2ff]"
                        />
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-slate-300 text-sm font-bold">Imagen / Video</label>
                          <button type="button" onClick={() => setShowFileSelectorModal(true)} className="text-xs bg-[#8c52ff] hover:bg-[#00c2ff] text-white px-3 py-1 rounded-full transition-colors">
                            Elegir de Archivos
                          </button>
                        </div>
                        
                        {selectedFileUrl ? (
                          <div className="relative rounded-xl overflow-hidden bg-slate-900 border border-slate-700 h-40 flex items-center justify-center group">
                            {selectedFileUrl.includes('.mp4') || selectedFileUrl.includes('.mov') ? (
                              <Video size={48} className="text-slate-500" />
                            ) : (
                              <img src={selectedFileUrl} className="w-full h-full object-cover opacity-80" />
                            )}
                            <button type="button" onClick={() => setSelectedFileUrl('')} className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <input
                            type="file"
                            accept="image/*,video/*"
                            required={!selectedFileUrl}
                            onChange={e => setDraftMedia(e.target.files?.[0] || null)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#8c52ff] file:text-white hover:file:bg-[#00c2ff]"
                          />
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-slate-300 text-sm font-bold mb-2">Descripción (Caption)</label>
                        <textarea
                          required
                          value={draftConfig.description}
                          onChange={e => setDraftConfig({...draftConfig, description: e.target.value})}
                          rows={4}
                          placeholder="Escribe el texto que acompañará a la publicación..."
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c2ff]"
                        />
                      </div>

                      <button 
                        type="submit"
                        disabled={isSavingDraft}
                        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isSavingDraft ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
                        Guardar en Inventario
                      </button>
                    </form>
                  </div>

                  {/* Grid de Borradores */}
                  <div className="lg:col-span-2">
                    <h3 className="text-xl font-bold text-white mb-4">Borradores Disponibles</h3>
                    {socialPosts.filter(p => p.status === 'draft').length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-xl bg-slate-800/30">
                        <FolderOpen size={48} className="mx-auto text-slate-600 mb-3" />
                        <p className="text-slate-400 font-medium">No hay borradores creados.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {socialPosts.filter(p => p.status === 'draft').map(post => (
                          <div key={post.id} className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl overflow-hidden flex flex-col group relative">
                            <div className="h-32 relative overflow-hidden bg-black/50 flex items-center justify-center">
                              {post.mediaUrl?.includes('.mp4') ? (
                                <video src={post.mediaUrl} className="w-full h-full object-cover opacity-60" />
                              ) : (
                                <img src={post.mediaUrl || ''} className="w-full h-full object-cover opacity-60" />
                              )}
                              <span className="absolute top-2 left-2 bg-black/50 backdrop-blur-md px-2 py-1 rounded text-xs text-white font-mono border border-slate-600/50">
                                DRAFT
                              </span>
                            </div>
                            <div className="p-3">
                              <p className="text-white font-bold text-sm mb-1 truncate">{post.title || 'Sin Título'}</p>
                              <p className="text-slate-400 text-xs line-clamp-2">{post.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {autoPosterTab === 'scheduler' && (
                <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6">
                  <form onSubmit={handleScheduleCampaign} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Configuración de Horario */}
                    <div className="lg:col-span-1 space-y-4">
                      <h3 className="text-xl font-bold text-white mb-4">1. Horario y Destino</h3>

                      <div>
                        <label className="block text-slate-300 text-sm font-bold mb-2">Seleccionar Red Social</label>
                        <select
                          required
                          value={campaignConfig.credentialId}
                          onChange={e => setCampaignConfig({...campaignConfig, credentialId: e.target.value})}
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c2ff]"
                        >
                          <option value="">-- Selecciona una Credencial --</option>
                          {socialCredentials.map(cred => (
                            <option key={cred.id} value={cred.id}>{cred.name} ({cred.platform})</option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-300 text-sm font-bold mb-2">¿Cuántos Días?</label>
                          <input
                            type="number"
                            min="1"
                            max="30"
                            required
                            value={campaignConfig.days}
                            onChange={e => setCampaignConfig({...campaignConfig, days: parseInt(e.target.value)})}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c2ff]"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-300 text-sm font-bold mb-2">Posts por Día</label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            required
                            value={campaignConfig.postsPerDay}
                            onChange={e => setCampaignConfig({...campaignConfig, postsPerDay: parseInt(e.target.value)})}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c2ff]"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-slate-300 text-sm font-bold mb-2">Fecha de Inicio</label>
                          <input
                            type="date"
                            required
                            value={campaignConfig.startDate}
                            onChange={e => setCampaignConfig({...campaignConfig, startDate: e.target.value})}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c2ff] [color-scheme:dark]"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-300 text-sm font-bold mb-2">Hora de Inicio</label>
                          <input
                            type="time"
                            required
                            value={campaignConfig.startTime}
                            onChange={e => setCampaignConfig({...campaignConfig, startTime: e.target.value})}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00c2ff] [color-scheme:dark]"
                          />
                        </div>
                      </div>

                      <div className="pt-6">
                        <button 
                          type="submit"
                          disabled={isGeneratingCampaign || selectedDraftIds.length !== (campaignConfig.days * campaignConfig.postsPerDay)}
                          className="w-full bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] hover:opacity-90 text-white font-bold py-4 rounded-xl shadow-lg shadow-purple-500/30 transition-all flex items-center justify-center gap-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isGeneratingCampaign ? <Loader2 size={24} className="animate-spin" /> : <Clock size={24} />}
                          Programar Campaña
                        </button>
                        <p className="text-center text-xs text-slate-500 mt-2">
                          Se necesitan {campaignConfig.days * campaignConfig.postsPerDay} posts. Has seleccionado {selectedDraftIds.length}.
                        </p>
                      </div>
                    </div>

                    {/* Selección de Contenido */}
                    <div className="lg:col-span-2 space-y-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-white">2. Selecciona los Posts</h3>
                        <span className="bg-slate-900 text-slate-300 px-3 py-1 rounded-full text-sm font-mono border border-slate-700">
                          {selectedDraftIds.length} / {campaignConfig.days * campaignConfig.postsPerDay} seleccionados
                        </span>
                      </div>
                      
                      {socialPosts.filter(p => p.status === 'draft').length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-xl bg-slate-900/50">
                          <p className="text-slate-400 font-medium mb-2">No tienes borradores en el Inventario.</p>
                          <button type="button" onClick={() => setAutoPosterTab('inventory')} className="text-[#00c2ff] hover:underline text-sm font-bold">
                            Ir al Inventario a crear posts
                          </button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                          {socialPosts.filter(p => p.status === 'draft').map((post, idx) => {
                            const isSelected = selectedDraftIds.includes(post.id!);
                            const selectedIndex = selectedDraftIds.indexOf(post.id!);
                            return (
                              <div 
                                key={post.id} 
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedDraftIds(selectedDraftIds.filter(id => id !== post.id));
                                  } else {
                                    if (selectedDraftIds.length < (campaignConfig.days * campaignConfig.postsPerDay)) {
                                      setSelectedDraftIds([...selectedDraftIds, post.id!]);
                                    }
                                  }
                                }}
                                className={`bg-slate-900 border rounded-xl overflow-hidden cursor-pointer transition-all relative ${
                                  isSelected ? 'border-[#00c2ff] shadow-[0_0_10px_rgba(0,194,255,0.3)]' : 'border-slate-700 hover:border-slate-500'
                                }`}
                              >
                                <div className="h-24 relative overflow-hidden bg-black/50">
                                  {post.mediaUrl?.includes('.mp4') ? (
                                    <video src={post.mediaUrl} className="w-full h-full object-cover opacity-70" />
                                  ) : (
                                    <img src={post.mediaUrl || ''} className="w-full h-full object-cover opacity-70" />
                                  )}
                                  
                                  {isSelected && (
                                    <div className="absolute inset-0 bg-[#00c2ff]/20 flex items-center justify-center">
                                      <div className="w-8 h-8 bg-[#00c2ff] rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                                        {selectedIndex + 1}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="p-2">
                                  <p className="text-white font-bold text-[10px] truncate">{post.title || `Borrador ${idx + 1}`}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              )}

              {autoPosterTab === 'calendar' && (
                <div className="space-y-4">
                  {socialPosts.filter(p => p.status !== 'draft').length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-xl bg-slate-800/30">
                      <CalendarRange size={48} className="mx-auto text-slate-600 mb-3" />
                      <p className="text-slate-400 font-medium">No hay posts programados en el calendario.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {socialPosts
                        .filter(p => p.status !== 'draft' && p.scheduledAt)
                        .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
                        .map(post => {
                          const isPending = post.status === 'pending';
                          return (
                            <div key={post.id} className={`bg-slate-800/50 backdrop-blur-xl border ${isPending ? 'border-amber-500/30' : 'border-emerald-500/30'} rounded-xl overflow-hidden flex flex-col`}>
                              <div className="h-48 relative overflow-hidden bg-black/50">
                                {post.mediaUrl?.includes('.mp4') ? (
                                  <video src={post.mediaUrl} className="w-full h-full object-cover opacity-80" />
                                ) : (
                                  <img src={post.mediaUrl || ''} className="w-full h-full object-cover opacity-80" />
                                )}
                                <div className="absolute top-2 right-2 flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-black/70 backdrop-blur-md">
                                  {isPending ? (
                                    <><Clock size={12} className="text-amber-400" /> <span className="text-amber-400">PROGRAMADO</span></>
                                  ) : (
                                    <><CheckCircle2 size={12} className="text-emerald-400" /> <span className="text-emerald-400">ENVIADO</span></>
                                  )}
                                </div>
                              </div>
                              <div className="p-4 flex-1 flex flex-col justify-between">
                                <p className="text-sm text-slate-300 line-clamp-3 mb-4">{post.description}</p>
                                <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/50">
                                  <p className="text-xs text-slate-400 mb-1">Fecha de Ejecución:</p>
                                  <p className="font-mono text-emerald-400 text-sm font-bold">
                                    {new Date(post.scheduledAt).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Selector de Archivos Modal */}
      {showFileSelectorModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-4xl border border-slate-700 shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b border-slate-700">
              <h3 className="text-xl font-bold text-white flex items-center gap-2"><FolderOpen className="text-[#8c52ff]" /> Seleccionar Archivo</h3>
              <button onClick={() => setShowFileSelectorModal(false)} className="text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-600 rounded-full p-2 transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {filesList.filter(f => f.type === 'image' || f.type === 'video').map(file => (
                  <div 
                    key={file.id} 
                    onClick={() => { setSelectedFileUrl(file.url); setShowFileSelectorModal(false); setCampaignMedia(null); }}
                    className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden cursor-pointer hover:border-[#00c2ff] hover:shadow-[0_0_15px_rgba(0,194,255,0.3)] transition-all group relative"
                  >
                    <div className="aspect-square flex items-center justify-center relative">
                      {file.type === 'image' ? (
                        <img src={file.url} alt={file.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <Video size={48} className="text-[#00c2ff]/50" />
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-[#8c52ff] text-white px-3 py-1 rounded-full text-xs font-bold">Seleccionar</span>
                      </div>
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium text-white truncate" title={file.name}>{file.name}</p>
                    </div>
                  </div>
                ))}
                {filesList.filter(f => f.type === 'image' || f.type === 'video').length === 0 && (
                  <div className="col-span-full py-12 text-center border-2 border-dashed border-slate-700 rounded-xl">
                    <p className="text-slate-400 font-medium">No tienes imágenes ni videos en tu Gestor de Archivos.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
